import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FETCH_FORMS_LIST } from 'actions/formActions';

const FormList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, allFormsList } = useSelector((state) => state.checklist);
    const [search, setSearch] = useState('');

    useEffect(() => {
        dispatch({ type: FETCH_FORMS_LIST });
    }, [dispatch]);

    // Group forms by stationId and filter out invalid or empty stations
    const groupedByStation = useMemo(() => {
        console.log('Grouping forms by stationId:', allFormsList);
        return allFormsList.reduce((acc, form) => {
            if (!form.stationId || typeof form.stationId !== 'string') {
                console.warn('Skipping form with invalid stationId:', form);
                return acc;
            }
            if (!acc[form.stationId]) {
                acc[form.stationId] = [];
            }
            acc[form.stationId].push(form);
            return acc;
        }, {});
    }, [allFormsList]);

    // Build filtered and structured station data
    const filteredList = useMemo(() => {
        const result = Object.entries(groupedByStation)
            .filter(([stationId, forms]) => {
                const isValid = forms.length > 0;
                if (!isValid) {
                    console.log(`Excluding stationId ${stationId} with no forms`);
                }
                return isValid;
            })
            .filter(([stationId]) => stationId.toLowerCase().includes(search.toLowerCase()))
            .map(([stationId, forms]) => {
                if (!forms[0]) {
                    console.warn(`No valid forms for stationId ${stationId}`);
                    return null;
                }
                const totalForms = forms.length;
                const approvedForms = forms.filter((f) => f.status === 'approved').length;
                const draftForms = forms.filter((f) => f.status === 'draft').length;
                const pendingForms = forms.filter((f) => f.status === 'pending').length;
                const completionPercentage = totalForms > 0 ? Math.round((approvedForms / totalForms) * 100) : 0;
                const { project_name, userName } = forms[0];
                console.log(
                    `Station ${stationId}: ${approvedForms} approved, ${draftForms} draft, ${pendingForms} pending, ${totalForms} total, ${completionPercentage}% complete`
                );
                return {
                    stationId,
                    project_name: project_name || 'N/A',
                    userName: userName || 'N/A',
                    status: approvedForms === totalForms && totalForms > 0 ? 'completed' : 'in-progress',
                    formCount: totalForms,
                    completionPercentage,
                    approvedCount: approvedForms,
                    draftCount: draftForms,
                    pendingCount: pendingForms,
                };
            })
            .filter((item) => item !== null)
            .sort((a, b) => a.stationId.localeCompare(b.stationId)); // Sort by stationId for consistency

        console.log('Filtered station list:', result);
        return result;
    }, [groupedByStation, search]);

    const getStatusBadge = (status) => {
        const variant = status === 'completed' ? 'success' : 'warning';
        return (
            <Badge bg={variant} className="text-uppercase">
                {status}
            </Badge>
        );
    };

    return (
        <div className="page-body">
            <div className="container-xl new_application">
                <div className="row mb-3">
                    <div className="col">
                        <h2 className="page-title">Form List</h2>
                    </div>
                </div>

                <Form.Control
                    type="text"
                    placeholder="Search by Station ID"
                    className="mb-3"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {loading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Table bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Station ID</th>
                                <th>Project Name</th>
                                <th>User</th>
                                <th>Number of Forms</th>
                                <th>Approved</th>
                                <th>Draft</th>
                                <th>Pending</th>
                                <th>Completion Percentage</th>
                                <th>Site Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.map((form) => (
                                <tr key={form.stationId}>
                                    <td
                                        role="button"
                                        style={{ color: '#0d6efd' }}
                                        onClick={() => navigate(`/formList/${form.stationId}`)}
                                    >
                                        {form.stationId}
                                    </td>
                                    <td>{form.project_name}</td>
                                    <td>{form.userName}</td>
                                    <td>{form.formCount}</td>
                                    <td>{form.approvedCount}</td>
                                    <td>{form.draftCount}</td>
                                    <td>{form.pendingCount}</td>
                                    <td>{form.completionPercentage}%</td>
                                    <td>{getStatusBadge(form.status)}</td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => navigate(`/formList/${form.stationId}`)}
                                        >
                                            View
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filteredList.length === 0 && (
                                <tr>
                                    <td colSpan="10" className="text-center text-muted">
                                        No forms found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default FormList;